import React, { useEffect } from 'react'
import { psqFormData } from './psqFormData'
import { getOwnProfile } from 'actions/profile-actions'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { submitPsq } from 'actions/psq-actions'
import PsqConfirmation from './PsqConfirmation'
import LoadingSpinnerWithContainer from 'components/generic/LoadingSpinnerWithContainer'
import UserMessageFullScreen from 'components/screens/UserMessageFullScreen'
import { getOwnProfileUrl, roundDownWtn } from 'utils'
import { MIN_AGE_FOR_PSQ } from 'constants/profile'
import i18n from 'constants/strings'
import { MessageTypes } from 'constants/message-screens'
import { useReloadIfCached } from 'hooks/use-reload-if-cached'
import withOnlyAllowLoggedIn from 'components/generic/hoc/withOnlyAllowLoggedIn'
import ErrorScreen from 'components/generic/ErrorScreen'
import Questionnaire from './Questionnaire'
import Wrapper from './Wrapper'
import {
  selectOwnProfile,
  selectOwnProfileError,
  selectOwnProfileLoading,
  selectPsqError,
  selectPsqLoading,
  selectPsqResults,
} from 'redux/selectors'
import { Trans, useTranslation } from 'react-i18next'
import { PersonIDEnum, PsqResults } from 'types/global'

const Psq = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const { tennisId } = useSelector((state: AppState) => state.user)
  const ownProfile = useSelector(selectOwnProfile)
  const profileLoading = useSelector(selectOwnProfileLoading)
  const profileError = useSelector(selectOwnProfileError)
  const psqLoading = useSelector(selectPsqLoading)
  const psqResults = useSelector(selectPsqResults)
  const psqError = useSelector(selectPsqError)

  useReloadIfCached()

  useEffect(() => {
    if (tennisId && !ownProfile) dispatch(getOwnProfile())
  }, [dispatch, tennisId, ownProfile])

  if (profileError) {
    return (
      <ErrorScreen
        type={MessageTypes.OWN_PROFILE_ERROR}
        message={profileError.message}
        backLink={window.location.href}
      />
    )
  }

  if (psqError)
    return (
      <UserMessageFullScreen
        messageType={MessageTypes.PSQ_SUBMIT_ERROR}
        backLink={getOwnProfileUrl()}
      />
    )

  if (profileLoading || !ownProfile || !tennisId || psqLoading)
    return (
      <Wrapper>
        <LoadingSpinnerWithContainer containerHeight={600} />
      </Wrapper>
    )

  if (ownProfile.wtnSingles || ownProfile.wtnDoubles)
    return (
      <UserMessageFullScreen
        messageType={MessageTypes.UNABLE_SUBMIT_PSQ}
        backLink={getOwnProfileUrl()}
      />
    )

  if (!ownProfile.eligibleForPsq)
    return (
      <UserMessageFullScreen
        messageType={MessageTypes.UNABLE_SUBMIT_PSQ}
        backLink={getOwnProfileUrl()}
        infoText={i18n.t('strMessageScreen.psqNotEligibleInfo')}
      />
    )

  if (typeof ownProfile.age === 'number' && ownProfile.age < MIN_AGE_FOR_PSQ)
    return (
      <UserMessageFullScreen
        messageType={MessageTypes.UNABLE_SUBMIT_PSQ}
        backLink={getOwnProfileUrl()}
        infoText={i18n.t('strMessageScreen.psqUnableSubmitMinAge', { minAge: MIN_AGE_FOR_PSQ })}
      />
    )

  return (
    <Wrapper>
      {psqResults ? (
        <PsqConfirmation
          results={psqResults}
          text={<ConfirmationText results={psqResults} />}
          cta={{
            text: t('screenCompleted.registered.button', { ns: 'strPsq' }),
            href: getOwnProfileUrl(),
          }}
        />
      ) : (
        <Questionnaire
          formData={psqFormData}
          onSubmit={answers => {
            dispatch(
              submitPsq(
                { identifier: ownProfile.id, type: PersonIDEnum.ID },
                {
                  answers,
                  gender: ownProfile?.sex,
                  age: ownProfile?.age,
                }
              )
            )
          }}
          withConfirmationModal
          withOnlyFillOnceAlert
        />
      )}
    </Wrapper>
  )
}

const ConfirmationText = ({ results }: { results: PsqResults }) => {
  return (
    <>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.registered.info1'}>
          Thank you for answering our questions.
        </Trans>
      </p>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.registered.info2'}>
          Your answers have been used to generate a <b>World Tennis Number</b> that you’ll be able
          to use until you start competing.
        </Trans>
      </p>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.registered.info3'}>
          Your <b>Singles number</b> has been set as{' '}
          <b>{{ wtnSingles: roundDownWtn(results.wtnSingles as number) }}</b>
          <br />
          Your <b>Doubles number</b> has been set as{' '}
          <b>{{ wtnDoubles: roundDownWtn(results.wtnDoubles as number) }}</b>
        </Trans>
      </p>
      <p>
        <Trans ns='strPsq' i18nKey={'screenCompleted.registered.info4'}>
          Enjoy your tennis.
        </Trans>
      </p>
    </>
  )
}

export default withOnlyAllowLoggedIn(Psq)
